import React from "react";

function PrivacyPolicy() {
  return (
    <div className="bg-background min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-20">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Privacy Policy
        </h1>

        <p className="text-muted-foreground mb-10">
          <strong>Effective Date:</strong> July 2026
        </p>

        <section className="space-y-10">

          <div>
            <p className="text-muted-foreground leading-8">
              Dataviqu is committed to protecting your privacy. This Privacy
              Policy explains how we collect, use, store, and protect your
              information.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Information We Collect
            </h2>

            <h3 className="text-xl font-medium mb-3">
              Personal Information
            </h3>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Full Name</li>
              <li>Email Address</li>
              <li>Phone Number</li>
              <li>Company Name</li>
              <li>Job Title</li>
            </ul>

            <h3 className="text-xl font-medium mt-8 mb-3">
              Technical Information
            </h3>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>IP Address</li>
              <li>Browser Information</li>
              <li>Device Information</li>
              <li>Operating System</li>
              <li>Website Usage Information</li>
              <li>Cookies</li>
            </ul>

            <h3 className="text-xl font-medium mt-8 mb-3">
              Project Information
            </h3>

            <p className="text-muted-foreground mb-3">
              When delivering services, we may collect:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Business requirements</li>
              <li>Project documentation</li>
              <li>Design files</li>
              <li>Datasets</li>
              <li>API credentials (when required)</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              How We Use Your Information
            </h2>

            <p className="text-muted-foreground mb-3">
              We use your information to:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Deliver our services</li>
              <li>Respond to enquiries</li>
              <li>Prepare quotations</li>
              <li>Process payments</li>
              <li>Improve our website</li>
              <li>Provide customer support</li>
              <li>Meet legal obligations</li>
            </ul>

            <p className="text-muted-foreground mt-4">
              We do not sell or rent your personal information.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Information Sharing
            </h2>

            <p className="text-muted-foreground mb-3">
              We may share information only with:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Payment providers</li>
              <li>Cloud hosting providers</li>
              <li>Technology partners involved in project delivery</li>
              <li>Government authorities where legally required</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Data Security
            </h2>

            <p className="text-muted-foreground mb-3">
              Dataviqu implements reasonable security measures including:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Secure servers</li>
              <li>Encryption where applicable</li>
              <li>Restricted access controls</li>
              <li>Regular monitoring</li>
            </ul>

            <p className="text-muted-foreground mt-4">
              Although we strive to protect your information, no online system
              is completely secure.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Data Retention
            </h2>

            <p className="text-muted-foreground mb-3">
              Personal information is retained only as long as necessary for:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Project completion</li>
              <li>Customer support</li>
              <li>Legal compliance</li>
              <li>Business record keeping</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Your Rights
            </h2>

            <p className="text-muted-foreground mb-3">
              You may request to:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Access your information</li>
              <li>Correct inaccurate information</li>
              <li>Delete your information (where legally applicable)</li>
              <li>Withdraw consent</li>
              <li>Request a copy of your personal data</li>
            </ul>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Third-Party Services
            </h2>

            <p className="text-muted-foreground leading-8">
              Our website may contain links to third-party websites or services.
              Dataviqu is not responsible for their privacy practices.
            </p>
          </div>

          <div>
            <h2 className="text-2xl font-semibold mb-4">
              Changes to This Policy
            </h2>

            <p className="text-muted-foreground leading-8">
              We may update this Privacy Policy periodically. The latest version
              will always be available on this page.
            </p>
          </div>

          <hr className="border-border" />

          <div>
            <h2 className="text-3xl font-bold mb-4">
              Cookie Policy
            </h2>

            <p className="text-muted-foreground leading-8">
              Dataviqu uses cookies to improve your browsing experience.
            </p>

            <ul className="list-disc pl-6 mt-4 space-y-2 text-muted-foreground">
              <li>Remember user preferences</li>
              <li>Improve website performance</li>
              <li>Analyse website traffic</li>
              <li>Enhance security</li>
              <li>Understand visitor behaviour</li>
            </ul>

            <p className="text-muted-foreground mt-4">
              You may disable cookies through your browser settings. Some
              website features may not function correctly if cookies are
              disabled.
            </p>
          </div>

          <hr className="border-border" />

          <div>
            <h2 className="text-3xl font-bold mb-4">
              Refund & Cancellation Policy
            </h2>

            <h3 className="text-xl font-medium mb-3">
              Project Cancellation
            </h3>

            <p className="text-muted-foreground leading-8">
              Projects may be cancelled before work begins. If work has already
              commenced, charges will apply based on the completed portion of
              the project.
            </p>

            <h3 className="text-xl font-medium mt-8 mb-3">
              Refund Policy
            </h3>

            <p className="text-muted-foreground mb-3">
              Refunds may be considered if:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Work has not started.</li>
              <li>Dataviqu is unable to deliver the agreed service.</li>
            </ul>

            <p className="text-muted-foreground mt-6 mb-3">
              Refunds are generally not available for:
            </p>

            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Custom software development</li>
              <li>AI and Machine Learning solutions</li>
              <li>Data Analytics projects</li>
              <li>Dashboard development</li>
              <li>UI/UX or graphic design work</li>
              <li>Consulting services</li>
              <li>Completed digital deliverables</li>
            </ul>

            <h3 className="text-xl font-medium mt-8 mb-3">
              Subscription Services
            </h3>

            <p className="text-muted-foreground">
              Monthly or recurring services may be cancelled before the next
              billing cycle.
            </p>

            <h3 className="text-xl font-medium mt-8 mb-3">
              Payment Disputes
            </h3>

            <p className="text-muted-foreground">
              Clients are encouraged to contact Dataviqu before initiating
              payment disputes.
            </p>
          </div>

          <hr className="border-border" />

          <div>
            <h2 className="text-3xl font-bold mb-4">
              Disclaimer
            </h2>

            <p className="text-muted-foreground leading-8">
              The information provided on this website is for general
              informational purposes only. While Dataviqu makes every effort to
              ensure the information is accurate and up to date, we make no
              guarantees regarding its accuracy, completeness, reliability,
              suitability, or availability.
            </p>

            <p className="text-muted-foreground mt-4 leading-8">
              Business decisions should not rely solely on information published
              on this website.
            </p>

            <p className="text-muted-foreground mt-4">
              Dataviqu shall not be responsible for:
            </p>

            <ul className="list-disc pl-6 mt-3 space-y-2 text-muted-foreground">
              <li>Third-party websites</li>
              <li>External links</li>
              <li>Client misuse of delivered solutions</li>
              <li>Financial or business losses arising from the use of our services</li>
            </ul>
          </div>

          <hr className="border-border" />

          <div>
            <h2 className="text-3xl font-bold mb-4">
              Copyright Notice
            </h2>

            <p className="text-muted-foreground leading-8">
              © 2026 Dataviqu. All Rights Reserved.
            </p>

            <p className="text-muted-foreground mt-4">
              All website content including text, graphics, logos, icons,
              images, source code, documents, and designs is the intellectual
              property of Dataviqu unless otherwise stated.
            </p>

            <p className="text-muted-foreground mt-4">
              No content may be copied, reproduced, modified, distributed or
              published without prior written permission.
            </p>
          </div>

          <hr className="border-border" />

          <div>
            <h2 className="text-3xl font-bold mb-4">
              Contact Us
            </h2>

            <p className="text-muted-foreground">
              <strong>Dataviqu</strong>
            </p>

            <p className="text-muted-foreground mt-2">
              Email:{" "}
              <a
                href="mailto:contact@dataviqu.com"
                className="text-primary hover:underline"
              >
                contact@dataviqu.com
              </a>
            </p>

            <p className="text-muted-foreground mt-2">
              Website:{" "}
              <a
                href="https://www.dataviqu.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                https://www.dataviqu.com
              </a>
            </p>

            <p className="text-muted-foreground mt-6">
              <strong>Business Hours</strong>
            </p>

            <p className="text-muted-foreground">
              Monday – Friday
            </p>

            <p className="text-muted-foreground">
              9:00 AM – 6:00 PM (IST)
            </p>

            <p className="text-muted-foreground mt-6">
              For any questions regarding our services, privacy, terms,
              payments, refunds, or legal matters, please contact us at{" "}
              <a
                href="mailto:contact@dataviqu.com"
                className="text-primary hover:underline"
              >
                contact@dataviqu.com
              </a>
              .
            </p>
          </div>

        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;